// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Segmentos de uma linha — o que a barra contínua precisa de saber
// ============================================================================
//
// PORQUE EXISTE
//   O mapa deixou de repetir a sigla do cliente em cada dia. Um itinerário passa
//   a ser **uma barra** com o nome escrito uma vez, o que exige saber onde cada
//   corrida começa e acaba. Este ficheiro calcula isso, e mais nada.
//
//   É a única aritmética de índices do mapa que pode estar errada em silêncio: um
//   segmento deslocado um dia desenha o itinerário sobre o dia errado e a grelha
//   continua com bom aspecto. Isolada, testa-se.
//
// AS QUATRO NATUREZAS DE UM DIA — e porque não são uma só
//   `itinerario`  serviço normal. Agrupa-se com os dias vizinhos do MESMO
//                 itinerário: é o que forma a barra.
//   `estado`      viatura indisponível (oficina, avaria, sinistro). Agrupa-se
//                 pelo motivo, e não tem motorista — nesses dias o motorista
//                 está em casa ou noutra viatura (D-055).
//   `rotacao`     chegada E partida no mesmo dia. **NUNCA agrupa**: é sempre um
//                 dia isolado, de propósito, porque é o corte entre duas barras.
//                 É onde a margem do objectivo O1 se ganha, e tem de se ver.
//   `parado`      sem viagem, mas **com local**: onde a viatura fica. Foi o
//                 cliente a exigi-lo — «nos dias sem viagem tem de ser definido
//                 onde o motorista dorme». Medido na folha real: dos 2 402 dias
//                 sem corrida, só 240 (10 %) têm local registado. É lacuna da
//                 folha actual, não descrição dela.
//                 ⚠️ Agrupa-se pela LOCALIDADE, e não numa chave só. Porque estar
//                 na base e estar parado em Madrid são coisas diferentes: a
//                 primeira custa zero, a segunda é alojamento pago (D-056,
//                 D-060). Fundir as duas numa barra escondia exactamente a
//                 distinção que interessa ver.
//   vazio         sem serviço e sem local. Não gera segmento — e é o que fica a
//                 faltar preencher.
// ============================================================================

import { BANDEIRA, type CelulaCompacta } from '@xlv-gva-mpt/dominio';

export type TipoSegmento = 'itinerario' | 'estado' | 'rotacao' | 'parado';

export interface Segmento {
  readonly tipo: TipoSegmento;
  /** Primeiro dia visível do segmento, já recortado à janela. Inclusivo. */
  readonly dia0: number;
  /** Exclusivo. */
  readonly dia1: number;
  /**
   * `true` quando `dia0` é o verdadeiro início da corrida, e não um corte da
   * janela. É o que decide se a aresta da barra leva canto redondo e tampa, ou
   * se fica a cortar — sem isto, cada deslocamento faria aparecer inícios falsos.
   */
  readonly abre: boolean;
  readonly fecha: boolean;
  /** Índice do itinerário, ou do estado da viatura. -1 na rotação. */
  readonly chave: number;
  /** Índice do cliente. -1 quando não há. */
  readonly cliente: number;
  /**
   * Índice da CORRIDA — a viagem. -1 quando não há.
   *
   * É por aqui que o mapa liga a mesma viagem em **duas viaturas diferentes**:
   * medido na folha real, 43 % das referências de reserva aparecem em mais de um
   * autocarro, 46 delas em dias sobrepostos.
   */
  readonly corrida: number;
}

export interface SegmentoMotorista {
  readonly dia0: number;
  readonly dia1: number;
  readonly abre: boolean;
  readonly fecha: boolean;
  readonly motorista: number;
  /** 2.º condutor simultâneo — tripulação dupla, art. 8.º n.º 5. */
  readonly motorista2: number;
}

/**
 * A natureza de um dia, para efeitos de agrupamento.
 *
 * ⚠️ A chave do itinerário é a **CORRIDA** (índice 10) e não a assinatura de rota
 * (índice 5). A diferença é medida: na folha real 2 679 assinaturas servem 15 132
 * corridas — cada assinatura reutilizada ~6 vezes. Agrupar pela assinatura fundia
 * duas viagens distintas e seguidas com a mesma origem/destino numa só barra.
 *
 * Onde não há corrida identificada cai-se na assinatura, que é o melhor que há.
 */
function natureza(c: CelulaCompacta | null): { tipo: TipoSegmento; chave: number } | null {
  if (c === null) return null;
  if (c[7] >= 0) return { tipo: 'estado', chave: c[7] };
  if ((c[4] & BANDEIRA.ROTACAO) !== 0) return { tipo: 'rotacao', chave: -1 };
  // Sem itinerário e sem corrida: a viatura está parada num sítio. A chave é a
  // LOCALIDADE — ver o cabeçalho: base e estrangeiro não podem partilhar barra.
  if (c[5] < 0 && c[10] < 0) return { tipo: 'parado', chave: c[1] };
  // ⚠️ ESPACOS DE CHAVE DISJUNTOS.
  //
  // A corrida e a rota sao duas tabelas diferentes, e ambas indexam de 0. Usar os
  // indices em cru fazia a corrida 0 e a rota 0 serem a MESMA chave — e o realce
  // por clique acendia barras sem relacao nenhuma. Apanhado por teste.
  //
  // A corrida fica positiva; a rota fica negativa a partir de -2, porque -1 esta
  // reservado a rotacao.
  return { tipo: 'itinerario', chave: c[10] >= 0 ? c[10] : -(c[5] + 2) };
}

/**
 * Segmentos da linha que se cruzam com `[janela0, janela1)`.
 *
 * Percorre a linha **inteira** e só depois recorta. É deliberado: para saber se
 * um segmento abre ou fecha dentro da janela é preciso conhecer os dias de fora,
 * e percorrer 365 posições por linha visível custa alguns milhares de iterações
 * por deslocamento — nada, ao lado do trabalho de layout.
 */
export function segmentos(
  linha: readonly (CelulaCompacta | null)[],
  janela0: number,
  janela1: number,
): Segmento[] {
  const saida: Segmento[] = [];
  let i = 0;
  while (i < linha.length) {
    const n = natureza(linha[i] ?? null);
    if (n === null) {
      i++;
      continue;
    }

    let fim = i + 1;
    // A rotação nunca agrupa: é sempre um dia. Ver o cabeçalho.
    if (n.tipo !== 'rotacao') {
      while (fim < linha.length) {
        const seg = natureza(linha[fim] ?? null);
        if (seg === null || seg.tipo !== n.tipo || seg.chave !== n.chave) break;
        fim++;
      }
    }

    const c0 = Math.max(i, janela0);
    const c1 = Math.min(fim, janela1);
    // `c1 > c0` e nao apenas a intersecao: com uma janela de largura zero — que
    // acontece no primeiro quadro, antes de o contentor ter tamanho — a
    // intersecao dava um segmento de 0 px de largura. Um segmento vazio nao e
    // «nada», e desenha-se.
    if (c1 > c0) {
      saida.push({
        tipo: n.tipo,
        dia0: c0,
        dia1: c1,
        abre: c0 === i,
        fecha: c1 === fim,
        chave: n.chave,
        cliente: linha[i]?.[0] ?? -1,
        corrida: linha[i]?.[10] ?? -1,
      });
    }

    i = fim;
  }
  return saida;
}

/**
 * Corridas de motorista da linha.
 *
 * Separadas das do itinerário porque **não coincidem**: o motorista pode trocar
 * a meio de um itinerário (o `trsf` da folha real, com local) e o mesmo motorista
 * pode atravessar dois itinerários seguidos. É o modelo operacional do negócio —
 * a viatura fica fora e os motoristas rodam — e desenhá-las como uma só coisa
 * esconderia exactamente aquilo que o planeador precisa de ver.
 *
 * Os dias em que a viatura está indisponível não entram: não têm motorista.
 */
export function segmentosMotorista(
  linha: readonly (CelulaCompacta | null)[],
  janela0: number,
  janela1: number,
): SegmentoMotorista[] {
  const saida: SegmentoMotorista[] = [];
  const chave = (c: CelulaCompacta | null): string | null => {
    if (c === null || c[7] >= 0 || c[2] < 0) return null;
    return `${c[2]}:${c[6]}`;
  };

  let i = 0;
  while (i < linha.length) {
    const k = chave(linha[i] ?? null);
    if (k === null) {
      i++;
      continue;
    }
    let fim = i + 1;
    while (fim < linha.length && chave(linha[fim] ?? null) === k) fim++;

    const c0 = Math.max(i, janela0);
    const c1 = Math.min(fim, janela1);
    if (c1 > c0) {
      saida.push({
        dia0: c0,
        dia1: c1,
        abre: c0 === i,
        fecha: c1 === fim,
        motorista: linha[i]?.[2] ?? -1,
        motorista2: linha[i]?.[6] ?? -1,
      });
    }
    i = fim;
  }
  return saida;
}
